import { useRef, useState } from "react";
import { CtaLink } from "../ui/CtaLink";
import { AnimatePresence, motion, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import { PlanScene } from "../three/PlanScene";
import { PlanShaderBg } from "../three/PlanShaderBg";
import { useI18n } from "../i18n";
import { LoopVideo } from "../ui/LoopVideo";
import bg from "../../public/upscaled-video.mp4";

export function Plan() {
  const { t } = useI18n();
  const steps = [
    { n: 1, title: t("plan.s1.title"), body: t("plan.s1.body") },
    { n: 2, title: t("plan.s2.title"), body: t("plan.s2.body") },
    { n: 3, title: t("plan.s3.title"), body: t("plan.s3.body") },
  ];

  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  // step 0 → 0-25% scroll, step 1 → 25-55%, step 2 → 55-100% (long hold so user can read & tap CTA)
  const stepF = useTransform(scrollYProgress, [0, 0.3, 0.6, 1], [0, 1, 2, 2]);
  const [step, setStep] = useState(0);
  const [stepFloat, setStepFloat] = useState(0);

  useMotionValueEvent(stepF, "change", (v) => {
    setStepFloat(v);
    const next = Math.max(0, Math.min(2, Math.round(v)));
    setStep((p) => (p !== next ? next : p));
  });

  return (
    <section ref={ref} className="relative" style={{ height: "460vh" }}>
       
      <div className="sticky top-0 h-screen overflow-hidden">
        {/* headline */}
        <div className="absolute top-24 inset-x-0 z-20 text-center px-6">
          <h2 className="text-4xl md:text-5xl">{t("plan.title")}</h2>
          <p className="mt-3 text-[var(--color-muted)]">{t("plan.sub")}</p>
        </div>

        {/* Base video background (looped) */}
        <div className="absolute inset-0 z-0">
          {bg ? <LoopVideo src={bg} fadeMs={1200} /> : null}
        </div>

        {/* Shader overlay — god-rays + drifting dust + moss glow, tinted by step */}
        <div className="absolute inset-0 z-[1] mix-blend-screen pointer-events-none">
          <PlanShaderBg stepFloat={stepFloat} />
        </div>

        {/* Morphing object scene — Doc → Brain → Kit, driven by scroll */}
        <div className="absolute inset-0 z-[3] pointer-events-none">
          <PlanScene step={stepFloat} />
        </div>

        {/* step card */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <AnimatePresence mode="wait" key={step}>
            {(() => {
              const s = steps[step] ?? steps[0]!;
              return (
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 80, rotateY: 25, scale: 0.5 }}
                  animate={{ opacity: 1, x: 0, rotateY: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -80, rotateY: -25, scale: 1.25 }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  style={{ perspective: 1200 }}
                  className="relative z-10 pointer-events-auto"
                >
                  <div className="w-[580px] max-w-[88vw] rounded-3xl border border-[var(--color-sage)]/60 bg-[var(--color-cream-light)]/85 backdrop-blur-xl p-10 shadow-[0_30px_80px_-40px_rgba(31,58,31,0.4)]">
                    <span className="font-serif text-[140px] leading-none text-[var(--color-sage)]/45 block -mb-4">
                      {s.n}
                    </span>
                    <h3 className="text-3xl md:text-4xl text-[var(--color-forest-deep)] leading-tight">
                      {s.title}
                    </h3>
                    <p className="mt-4 text-[var(--color-muted)] leading-relaxed">{s.body}</p>

                    {step === 2 && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.12, duration: 0.5 }}
                        className="mt-6 flex items-center gap-3 flex-wrap"
                      >
                        <CtaLink cta="survey" block="plan_step3" to="/survey" className="pill-btn !py-2 !px-5 text-sm">
                          {t("hero.cta.survey")}
                        </CtaLink>
                        <CtaLink cta="waitlist" block="plan_step3" to="/waitlist" className="pill-outline !py-2 !px-5 text-sm">
                          {t("hero.cta.join")}
                        </CtaLink>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              );
            })()}
          </AnimatePresence>
        </div>

        {/* progress dots */}
        <div className="absolute bottom-10 inset-x-0 flex justify-center gap-3 z-20">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === step ? "w-10 bg-[var(--color-forest-deep)]" : "w-3 bg-[var(--color-forest-deep)]/25"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
